import { useCallback, useEffect, useRef } from "react";

/**
 * Default idle window: 20 minutes of no user activity before forced logout.
 * Exported so the AppShell (or tests) can reuse the same constant.
 */
export const IDLE_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * How long before the final logout we should surface the warning modal.
 * 1 minute lead-time means the modal appears at the 19-minute mark.
 */
export const WARNING_BEFORE_MS = 60 * 1000;

/**
 * Minimum interval between activity-driven timer resets. We don't want
 * `mousemove` firing 60×/second to constantly tear down and rebuild the
 * underlying setTimeout handles.
 */
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * DOM events that count as "the user is still here". Registered in capture
 * phase with `passive: true` so we never block scrolling or wheel events.
 */
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
] as const;

export interface UseIdleTimeoutOptions {
  /** Total inactivity window before `onTimeout` fires. */
  idleMs: number;
  /** Lead time before timeout that `onWarning` fires. Must be < idleMs. */
  warningMs: number;
  /** Called once when the warning timer elapses (idleMs - warningMs). */
  onWarning: () => void;
  /** Called once when the full idle window elapses with no acknowledgement. */
  onTimeout: () => void;
  /** Optional notification for every (throttled) activity event. */
  onActivity?: () => void;
  /** When false the hook is fully inert (e.g. on the login page). */
  enabled: boolean;
}

export interface UseIdleTimeoutResult {
  /**
   * Force the timers back to zero. Call this from the warning modal's
   * "Okay" button so acknowledgement extends the session by another full
   * `idleMs` window.
   */
  reset: () => void;
}

/**
 * Tracks user inactivity and fires `onWarning` once before `onTimeout`.
 *
 * Behaviour:
 *  - While `enabled`, listens for activity events on the document and
 *    (throttled to once every ~1 s) restarts both timers.
 *  - Fires `onWarning` at `(idleMs - warningMs)`.
 *  - Fires `onTimeout` at `idleMs` if `reset()` has not been called.
 *  - After `onWarning` fires, raw activity events stop restarting the
 *    timers — only an explicit `reset()` (i.e. the user clicked "Okay")
 *    will dismiss the warning and start a fresh window. Without this, a
 *    stray mousemove on the modal overlay would silently cancel the
 *    logout and the warning would never close on its own.
 *  - Tears down all listeners + pending timers when `enabled` flips to
 *    false or the component unmounts (so logging out really does stop
 *    the clock).
 */
export function useIdleTimeout(
  opts: UseIdleTimeoutOptions,
): UseIdleTimeoutResult {
  const { idleMs, warningMs, onWarning, onTimeout, onActivity, enabled } =
    opts;

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetAtRef = useRef<number>(0);
  const warningFiredRef = useRef<boolean>(false);

  // Keep latest callbacks in refs so we don't rebind document listeners
  // every time the parent re-renders with a fresh closure.
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);
  const onActivityRef = useRef(onActivity);
  onWarningRef.current = onWarning;
  onTimeoutRef.current = onTimeout;
  onActivityRef.current = onActivity;

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current !== null) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (timeoutTimerRef.current !== null) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    warningFiredRef.current = false;
    const warnAt = Math.max(0, idleMs - warningMs);
    warningTimerRef.current = setTimeout(() => {
      warningFiredRef.current = true;
      onWarningRef.current();
    }, warnAt);
    timeoutTimerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, idleMs);
  }, [idleMs, warningMs, clearTimers]);

  const reset = useCallback(() => {
    lastResetAtRef.current = Date.now();
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      warningFiredRef.current = false;
      return;
    }

    // Kick off the first window the moment the hook becomes enabled (e.g.
    // immediately after login).
    lastResetAtRef.current = Date.now();
    startTimers();

    const handleActivity = () => {
      onActivityRef.current?.();
      // After the warning is showing only an explicit reset() restarts
      // the clock. See class docs above for why.
      if (warningFiredRef.current) return;
      const now = Date.now();
      if (now - lastResetAtRef.current < ACTIVITY_THROTTLE_MS) return;
      lastResetAtRef.current = now;
      startTimers();
    };

    const listenerOpts: AddEventListenerOptions = {
      passive: true,
      capture: true,
    };
    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, handleActivity, listenerOpts);
    }

    return () => {
      clearTimers();
      warningFiredRef.current = false;
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, handleActivity, { capture: true });
      }
    };
  }, [enabled, startTimers, clearTimers]);

  return { reset };
}
